import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  Layers3,
  Search,
  Users,
  X,
} from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import Module3DIcon from "../../components/marketing/Module3DIcon";
import { module3dAsset } from "../../components/marketing/module3dAssets";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { isPlainBookDemoActivation } from "../../components/marketing/shared";
import { track } from "../../lib/analytics";
import { cn } from "../../lib/utils";
import { Reveal } from "./parts1";
import { MOD_STAGGER } from "./parts2";
import { ModuleFeatureCard } from "./parts3";
import ModuleBriefDialog, { moduleBriefPath } from "./ModuleBriefDialog";
import { MODULE_CATALOG, MODULE_WORKSPACES } from "./moduleCatalog";

const ALL_WORKSPACES = Object.freeze({
  k: "all",
  label: "All capabilities",
  title: "The complete Vidyapeeth360 catalogue",
  headline: "Move from one school job to the next without losing the record, owner or evidence behind it.",
  desc: "Browse every documented module, feature, report, integration, mobile experience and governed Aira capability in one place.",
  owners: ["School leaders", "Operational teams", "Teachers and families"],
  color: "#2563EB",
  icon: Layers3,
  modules: MODULE_CATALOG,
});

const RETIRED_MODULE_COPY = Object.freeze({
  badge: new Set(["Connected school journeys", "Explore Vidyapeeth360 Modules"]),
  heading: new Set([
    "Six school journeys. One connected operating record.",
    "A documented catalogue for the school day",
    // CMS-002 (2026-08-19): "every school workflow" claimed total coverage of a school's
    // operations that the 115-entry catalogue does not deliver.
    "Every school workflow, explained feature by feature.",
  ]),
});

const searchableText = (mod) => [
  mod.name,
  mod.oneLiner,
  mod.kind,
  mod.publicStatus,
  mod.workspaceLabel,
  ...(mod.roles || []),
  mod.detail?.what,
  ...(mod.detail?.bullets || []),
].filter(Boolean).join(" ").toLocaleLowerCase("en-IN");

function useDesktopFeatureLimit() {
  const read = () => typeof window === "undefined" || window.innerWidth >= 1024;
  const [desktop, setDesktop] = useState(read);

  useEffect(() => {
    const media = window.matchMedia?.("(min-width: 1024px)");
    const update = () => setDesktop(media ? media.matches : window.innerWidth >= 1024);
    update();
    media?.addEventListener?.("change", update);
    window.addEventListener("resize", update);
    return () => {
      media?.removeEventListener?.("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return desktop ? 8 : 4;
}

// Below 640px a card navigates straight to /modules/:key instead of opening the brief.
// A phone-width dialog is a full-screen takeover with no URL — not shareable, not bookmarkable,
// and Back does not close it — which is strictly worse than the real page it would summarise.
// (Rationale + the full modal contract live in ./ModuleBriefDialog.js.)
export const BRIEF_MIN_WIDTH_QUERY = "(min-width: 640px)";

function useBriefViewport() {
  const read = () => {
    if (typeof window === "undefined") return true;
    const media = window.matchMedia?.(BRIEF_MIN_WIDTH_QUERY);
    return media ? media.matches : window.innerWidth >= 640;
  };
  const [enabled, setEnabled] = useState(read);

  useEffect(() => {
    const media = window.matchMedia?.(BRIEF_MIN_WIDTH_QUERY);
    const update = () => setEnabled(media ? media.matches : window.innerWidth >= 640);
    update();
    media?.addEventListener?.("change", update);
    window.addEventListener("resize", update);
    return () => {
      media?.removeEventListener?.("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return enabled;
}

function WorkspaceCoverage({ workspace, modules }) {
  const kinds = useMemo(() => {
    const counts = new Map();
    modules.forEach((mod) => counts.set(mod.kind, (counts.get(mod.kind) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [modules]);
  const visualModules = modules.slice(0, 3);
  const WorkspaceIcon = workspace.icon;

  return (
    <aside className="mod-chip rounded-2xl border p-[18px]" aria-label={`${workspace.title} coverage`}>
      <div className="flex items-center gap-3 border-b border-line pb-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: `${workspace.color}14` }} aria-hidden="true">
          <WorkspaceIcon className="h-5 w-5" style={{ color: workspace.color }} />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-ink">Workspace coverage</p>
          <p className="mt-0.5 text-[11px] leading-4 text-ink-subtle">{modules.length} matching of {workspace.modules.length} documented</p>
        </div>
      </div>

      <div className="mt-4 flex items-center" aria-hidden="true">
        {visualModules.map((mod, index) => (
          <Module3DIcon
            key={mod.key}
            Icon={mod.Icon}
            src={module3dAsset(mod)}
            color={workspace.color}
            size="sm"
            className={cn("rounded-xl bg-surface shadow-sm", index > 0 && "-ml-2")}
          />
        ))}
        <span className="ml-3 text-[11px] font-semibold text-ink-subtle">Each capability has its own page</span>
      </div>

      <dl className="mt-4 space-y-3">
        {kinds.map(([kind, count]) => (
          <div key={kind} className="flex items-center justify-between gap-3 text-[12px]">
            <dt className="text-ink-2">{kind}</dt>
            <dd className="font-bold tabular-nums text-ink">{count}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-4 border-t border-line pt-4">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-subtle">
          <Users className="h-3.5 w-3.5" aria-hidden="true" /> Typical teams
        </p>
        <p className="mt-2 text-[12px] leading-5 text-ink-2">{workspace.owners.join(" · ")}</p>
      </div>

      <Link
        to={workspace.k === "all" ? "/modules" : `/modules?group=${workspace.k}`}
        data-testid="explore-all-modules"
        onClick={() => track("landing_module_catalogue_opened", { workspace: workspace.k })}
        className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-line bg-surface px-3 text-[12.5px] font-semibold text-primary transition-[border-color,background-color] hover:border-primary/30 hover:bg-primary/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
      >
        {workspace.k === "all" ? "Open complete catalogue" : `Open the ${workspace.label} catalogue`} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </aside>
  );
}

/**
 * Landing-page progressive module explorer.
 *
 * The component deliberately consumes the canonical MODULE_CATALOG / MODULE_WORKSPACES registry:
 * the landing page and /modules never maintain separate feature lists. Only the selected workspace
 * is painted initially; search moves to the full catalogue and expansion exposes every matching
 * canonical route without an internal scroll area.
 */
export default function LandingModuleExplorer({ content = {} }) {
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduce = Boolean(prefersReducedMotion || paused);
  const initialLimit = useDesktopFeatureLimit();
  const briefEnabled = useBriefViewport();
  const [workspaceKey, setWorkspaceKey] = useState("academics");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [brief, setBrief] = useState(null);
  const viewMoreRef = useRef(null);
  const workspaceRefs = useRef([]);

  const workspaces = useMemo(() => [ALL_WORKSPACES, ...MODULE_WORKSPACES], []);
  const workspace = workspaces.find((item) => item.k === workspaceKey) || workspaces[0];
  const needle = query.trim().toLocaleLowerCase("en-IN");
  const filteredModules = useMemo(
    () => workspace.modules.filter((mod) => !needle || searchableText(mod).includes(needle)),
    [needle, workspace],
  );
  const visibleModules = expanded ? filteredModules : filteredModules.slice(0, initialLimit);
  const ActiveWorkspaceIcon = workspace.icon;

  useEffect(() => setExpanded(false), [initialLimit, needle, workspaceKey]);

  const badge = content.badge && !RETIRED_MODULE_COPY.badge.has(content.badge)
    ? content.badge
    : "Explore Vidyapeeth360 modules";
  const heading = content.heading && !RETIRED_MODULE_COPY.heading.has(content.heading)
    ? content.heading
    : "Every documented capability, explained feature by feature.";
  const subhead = content.subhead && !content.subhead.startsWith("See how work moves from first input")
    ? content.subhead
    : `Explore ${MODULE_CATALOG.length} documented capabilities across ${MODULE_WORKSPACES.length} school workspaces. See what each capability does, who uses it and what rollout or provider setup it may require.`;

  const selectWorkspace = (nextKey) => {
    setWorkspaceKey(nextKey);
    setExpanded(false);
    track("landing_module_workspace_selected", { workspace: nextKey });
  };

  const onWorkspaceKeyDown = (event, index) => {
    const last = workspaces.length - 1;
    let next = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    selectWorkspace(workspaces[next].k);
    workspaceRefs.current[next]?.focus();
  };

  const updateQuery = (event) => {
    const nextQuery = event.target.value;
    // Search is a global discovery action. Moving to "All capabilities" makes that scope visible
    // instead of silently returning zero results from whichever workspace happened to be open.
    if (nextQuery.trim() && !query.trim()) setWorkspaceKey("all");
    setQuery(nextQuery);
  };

  // ONE place decides what a card activation means. Returning true makes the card call
  // preventDefault() so its <a href> stands down; anything else lets the anchor navigate — which
  // covers ⌘/ctrl/shift/middle-click "open in a new tab", crawlers, and the phone-width path.
  const openBrief = useCallback((mod, event) => {
    if (!isPlainBookDemoActivation(event)) return false;
    if (!briefEnabled) return false;
    track("module_brief_opened", { module: mod.key, workspace: mod.workspace, placement: "landing" });
    setBrief(mod);
    return true;
  }, [briefEnabled]);

  const toggleExpanded = () => {
    if (!expanded) {
      setExpanded(true);
      track("landing_module_results_expanded", { workspace: workspace.k, results: filteredModules.length });
      return;
    }
    const before = viewMoreRef.current?.getBoundingClientRect().top ?? 0;
    setExpanded(false);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const after = viewMoreRef.current?.getBoundingClientRect().top ?? before;
      if (Math.abs(after - before) > 1) {
        window.scrollBy({ top: after - before, left: 0, behavior: reduce ? "auto" : "smooth" });
      }
    }));
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 md:py-16 lg:px-8 lg:py-20" data-testid="landing-module-explorer">
      <Reveal className="mx-auto max-w-[52rem] text-center">
        <span className="inline-flex min-h-8 items-center rounded-full border border-primary/15 bg-primary/[0.07] px-3 text-xs font-semibold text-primary">
          <BrandCopy className="mx-1">{badge}</BrandCopy>
        </span>
        <h2 id="modules-heading" className="mt-4 text-balance font-heading text-[clamp(2rem,4.1vw,3.75rem)] font-bold leading-[1.02] tracking-[-0.042em] text-ink">
          <BrandCopy>{heading}</BrandCopy>
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-pretty text-base leading-7 text-ink-2 sm:text-lg sm:leading-8">
          <BrandCopy>{subhead}</BrandCopy>
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12.5px] font-semibold text-ink-2" aria-label="What every catalogue page includes">
          <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-positive" aria-hidden="true" /> Canonical product scope</span>
          <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-positive" aria-hidden="true" /> Role and availability context</span>
          <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-positive" aria-hidden="true" /> One page for every capability</span>
        </div>
      </Reveal>

      <div className="mx-auto mt-9 max-w-3xl">
        <label htmlFor="landing-module-search" className="sr-only">Search all modules and capabilities</label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
          <input
            id="landing-module-search"
            type="search"
            value={query}
            onChange={updateQuery}
            onBlur={() => {
              if (needle) track("landing_module_search_used", { query_length: needle.length, results: filteredModules.length });
            }}
            placeholder="Search fees, admissions, payroll, attendance, transport…"
            autoComplete="off"
            className="h-[54px] w-full rounded-2xl border border-line bg-surface py-3 pl-12 pr-28 text-[14px] text-ink shadow-[0_14px_40px_-28px_rgba(15,23,42,0.65)] outline-none transition-[border-color,box-shadow] placeholder:text-ink-subtle focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/10 sm:text-[15px]"
          />
          {query ? (
            <button type="button" onClick={() => setQuery("")} aria-label="Clear module search"
              className="absolute right-3 top-1/2 inline-flex min-h-9 -translate-y-1/2 items-center gap-1 rounded-lg px-2 text-[11.5px] font-semibold text-ink-subtle hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45">
              Clear <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : (
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-ink-subtle">Search all {MODULE_CATALOG.length}</span>
          )}
        </div>
      </div>

      <nav role="tablist" aria-label="Module workspaces" className="relative mt-7">
        <div className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-0.5 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:justify-center lg:overflow-visible">
          {workspaces.map((item, index) => {
            const active = workspace.k === item.k;
            const ItemIcon = item.icon;
            return (
              <button
                key={item.k}
                type="button"
                role="tab"
                id={`landing-module-tab-${item.k}`}
                aria-selected={active}
                aria-controls="landing-module-panel"
                tabIndex={active ? 0 : -1}
                ref={(element) => { workspaceRefs.current[index] = element; }}
                onClick={() => selectWorkspace(item.k)}
                onKeyDown={(event) => onWorkspaceKeyDown(event, index)}
                data-testid={`landing-module-filter-${item.k}`}
                // color/background-color are deliberately NOT transitioned: the active chip's dark
                // gradient is a background-IMAGE, which is not animatable and disappears the frame a
                // chip deactivates — transitioning `color` left the label easing from pure white over
                // the pale page for ~200ms (measured 1.06:1; CI axe caught the mid-flight frame).
                // Text and background must swap atomically in the same frame as the gradient.
                className={cn(
                  "group relative inline-flex min-h-[48px] shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-2xl border px-4 text-[13px] font-semibold transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-muted",
                  active
                    ? "border-transparent text-white shadow-[0_8px_22px_-10px_rgba(15,23,42,0.5)]"
                    : "border-line bg-surface text-ink-2 hover:border-primary/35 hover:bg-primary/[0.04] hover:text-ink",
                )}
                // VIS-7: the dark half of the active chip is the ONE hero-navy scene colour
                // (--vp-hero-navy, index.css / DESIGN.md), not a near-miss slate hex — the same
                // token the hero canopy, the module deep-dive stage and the phone bezels use.
                style={active ? {
                  // A solid navy UNDER the gradient: if the gradient ever fails to paint (an
                  // unresolvable token/color-mix makes the whole declaration invalid), the white
                  // label must still sit on navy — CI's axe pass caught it measuring 1.05:1
                  // against the pale card behind a transparent chip. ORDER MATTERS: React applies
                  // these in object order onto the CSSOM, and the `background` SHORTHAND resets the
                  // background-color longhand to transparent — so the shorthand must come FIRST and
                  // `backgroundColor` after it, or the navy fallback is silently wiped (measured:
                  // computed background-color was rgba(0,0,0,0) with the previous order). The var()
                  // fallbacks keep the gradient itself valid even before index.css tokens land.
                  background: "linear-gradient(135deg, color-mix(in srgb, " + item.color + " 55%, var(--vp-hero-navy, #080f20)), var(--vp-hero-navy, #080f20))",
                  backgroundColor: "var(--vp-hero-navy, #080f20)",
                  boxShadow: "0 8px 22px -10px color-mix(in srgb, " + item.color + " 62%, var(--vp-hero-navy, #080f20))",
                } : undefined}
              >
                <ItemIcon className={cn("relative z-10 h-4 w-4", active ? "text-white" : "text-ink-subtle group-hover:text-ink")} aria-hidden="true" />
                <span className="relative z-10">{item.label}</span>
                <span className={cn("relative z-10 text-[10.5px] tabular-nums", active ? "text-white/90" : "text-ink-subtle")}>{item.modules.length}</span>
              </button>
            );
          })}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-muted/80 to-transparent lg:hidden" aria-hidden="true" />
      </nav>

      <div className="relative mx-auto mt-4 max-w-[77.5rem]">
        <div className="pointer-events-none absolute -top-4 left-1/2 h-8 w-[58%] -translate-x-1/2 rounded-full bg-primary/25 blur-2xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -inset-x-6 -bottom-6 -top-3 rounded-[36px] bg-primary/[0.06] blur-3xl" aria-hidden="true" />
        <m.div
          id="landing-module-panel"
          role="tabpanel"
          aria-labelledby={`landing-module-tab-${workspace.k}`}
          key={workspace.k}
          initial={reduce ? false : { opacity: 0, transform: "translateY(16px)" }}
          animate={reduce ? undefined : { opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          className="mod-surface relative overflow-hidden rounded-[28px] border shadow-[0_22px_60px_-18px_rgba(29,78,216,0.16)]"
          data-testid="landing-module-panel"
        >
          <div className="absolute left-6 right-6 top-0 h-[3px] rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${workspace.color}, transparent)` }} aria-hidden="true" />
          <div className="px-5 py-7 sm:px-8 sm:py-9 lg:px-12 lg:py-10">
            <div className="flex flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-start sm:gap-5">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl" style={{ background: `${workspace.color}14` }} aria-hidden="true">
                <ActiveWorkspaceIcon className="h-7 w-7" style={{ color: workspace.color }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-heading text-[23px] font-bold tracking-tight text-ink sm:text-[28px]">{workspace.title}</h3>
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold text-ink" style={{ background: `${workspace.color}14` }}>
                    {filteredModules.length} {filteredModules.length === 1 ? "capability" : "capabilities"}
                  </span>
                </div>
                <p className="mt-1.5 max-w-3xl font-heading text-[15px] font-semibold leading-snug text-ink sm:text-[17px]">{workspace.headline}</p>
                <p className="mt-1.5 max-w-3xl text-[13.5px] leading-relaxed text-ink-2">{workspace.desc}</p>
              </div>
            </div>

            <p className="sr-only" aria-live="polite">{filteredModules.length} capabilities found in {workspace.title}</p>

            {filteredModules.length ? (
              <div className="mt-6 grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-7">
                <div className="min-w-0">
                  <m.div
                    key={`${workspace.k}-${needle || "default"}-grid`}
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                    variants={reduce ? undefined : MOD_STAGGER}
                    initial={reduce ? false : "hidden"}
                    animate="show"
                    data-testid="landing-module-results"
                  >
                    {visibleModules.map((mod) => (
                      <ModuleFeatureCard
                        key={mod.key}
                        Icon={mod.Icon}
                        name={mod.name}
                        desc={mod.oneLiner}
                        badge={mod.badge}
                        color={mod.aiFeature ? mod.groupColor : workspace.color}
                        reduce={reduce}
                        meta={`For ${(mod.roles || []).slice(0, 2).join(" · ") || "authorised school teams"}`}
                        actionLabel="Full details"
                        testId={`landing-module-card-${mod.key}`}
                        art={module3dAsset(mod)}
                        to={moduleBriefPath(mod)}
                        onOpen={(event) => {
                          if (openBrief(mod, event)) return true;
                          // The anchor is about to navigate for real — keep `module_card_opened`
                          // meaning exactly what it always meant (a card reached the module page),
                          // so the brief funnel never double-counts a single activation.
                          track("module_card_opened", {
                            module: mod.key,
                            source_group: mod.group,
                            workspace: mod.workspace,
                            placement: "landing",
                          });
                          return false;
                        }}
                      />
                    ))}
                  </m.div>

                  {filteredModules.length > initialLimit && (
                    <div className="mt-6 flex justify-center">
                      <button
                        ref={viewMoreRef}
                        type="button"
                        onClick={toggleExpanded}
                        aria-expanded={expanded}
                        data-testid="module-view-more"
                        className="group inline-flex min-h-11 items-center gap-2 rounded-full border px-5 text-[14px] font-semibold text-ink transition-[transform,background-color,border-color,box-shadow] hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                        style={{ borderColor: `${workspace.color}40`, background: `${workspace.color}0A` }}
                      >
                        {expanded ? "Show fewer" : `Show all ${filteredModules.length} capabilities`}
                        <ChevronDown className={cn("h-4 w-4 text-ink-subtle transition-transform duration-300", expanded && "rotate-180")} aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
                <WorkspaceCoverage workspace={workspace} modules={filteredModules} />
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-dashed border-line bg-muted/55 px-5 py-12 text-center" data-testid="landing-module-empty">
                <BarChart3 className="mx-auto h-7 w-7 text-ink-subtle" aria-hidden="true" />
                <p className="mt-3 font-heading text-[17px] font-semibold text-ink">No matching capability found</p>
                <p className="mx-auto mt-1 max-w-lg text-[13px] leading-6 text-ink-subtle">Try a school job such as fee collection, timetable, payroll, transport or parent communication.</p>
                <button type="button" onClick={() => { setQuery(""); setWorkspaceKey("all"); }}
                  className="mt-4 inline-flex min-h-11 items-center rounded-full border border-primary/25 bg-surface px-4 text-[13px] font-semibold text-primary hover:bg-primary/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45">
                  See all capabilities
                </button>
              </div>
            )}
          </div>
        </m.div>
      </div>

      <ModuleBriefDialog
        mod={brief}
        open={Boolean(brief)}
        accent={brief ? (brief.aiFeature ? brief.groupColor : workspace.color) : undefined}
        onClose={() => setBrief(null)}
      />
    </div>
  );
}
